"use strict";
import { createPublicClient, http, formatUnits } from "viem";
import { bsc, bscTestnet } from "viem/chains";
import { getBscContracts } from "constants/bscContracts";
import { getDexTokenList } from "constants/topazTokenList";
import { getChainInfo } from "features/chains/chainInfo";
import { RPCType, UniverseChainId } from "features/chains/types";
import { getCommonIntermediaries } from "features/routing/constants/intermediaries";
import { routingWarn, routingError } from "features/routing/utils/debug";
const ZERO_ADDRESS = "0x0000000000000000000000000000000000000000";
const PAIR_FACTORY_ABI = [
  {
    type: "function",
    name: "getPool",
    stateMutability: "view",
    inputs: [
      { name: "tokenA", type: "address" },
      { name: "tokenB", type: "address" },
      { name: "stable", type: "bool" }
    ],
    outputs: [{ name: "pool", type: "address" }]
  }
];
const PAIR_ABI = [
  {
    type: "function",
    name: "getReserves",
    stateMutability: "view",
    inputs: [],
    outputs: [
      { name: "reserve0", type: "uint112" },
      { name: "reserve1", type: "uint112" },
      { name: "blockTimestampLast", type: "uint32" }
    ]
  },
  {
    type: "function",
    name: "token0",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "address" }]
  },
  {
    type: "function",
    name: "token1",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "address" }]
  }
];
const ERC20_ABI = [
  { type: "function", name: "decimals", stateMutability: "view", inputs: [], outputs: [{ type: "uint8" }] },
  { type: "function", name: "symbol", stateMutability: "view", inputs: [], outputs: [{ type: "string" }] },
  { type: "function", name: "name", stateMutability: "view", inputs: [], outputs: [{ type: "string" }] }
];
const tokenMetadataCacheByChain = /* @__PURE__ */ new Map();
function getViemChain(chainId) {
  return chainId === UniverseChainId.Sepolia ? bscTestnet : bsc;
}
function getRpcUrl(chainId) {
  const info = getChainInfo(chainId);
  return info.rpcUrls?.[RPCType.Default]?.http?.[0] ?? info.rpcUrls?.[RPCType.Public]?.http?.[0] ?? info.rpcUrls?.[RPCType.Interface]?.http?.[0];
}
function makeClient(chainId) {
  const rpcUrl = getRpcUrl(chainId);
  if (!rpcUrl) return null;
  return createPublicClient({
    chain: getViemChain(chainId),
    transport: http(rpcUrl)
  });
}
function dedupePairs(pairs) {
  const seen = /* @__PURE__ */ new Set();
  const out = [];
  for (const [a, b] of pairs) {
    if (!a || !b) continue;
    const al = a.toLowerCase();
    const bl = b.toLowerCase();
    if (al === bl || al === ZERO_ADDRESS || bl === ZERO_ADDRESS) continue;
    const key = al < bl ? `${al}-${bl}` : `${bl}-${al}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push([al, bl]);
  }
  return out;
}
function lookupKnownToken(chainId, address) {
  const lower = address.toLowerCase();
  return getDexTokenList(chainId).find((t) => t.address.toLowerCase() === lower);
}
function getTokenCacheForChain(chainId) {
  let chainCache = tokenMetadataCacheByChain.get(chainId);
  if (!chainCache) {
    chainCache = /* @__PURE__ */ new Map();
    tokenMetadataCacheByChain.set(chainId, chainCache);
  }
  return chainCache;
}
async function loadTokenMetadata(client, chainId, addresses) {
  const chainCache = getTokenCacheForChain(chainId);
  const map = /* @__PURE__ */ new Map();
  const missing = [];
  for (const addr of addresses) {
    const lower = addr.toLowerCase();
    const cached = chainCache.get(lower);
    if (cached) {
      map.set(lower, cached);
      continue;
    }
    const known = lookupKnownToken(chainId, lower);
    if (known) {
      const meta = {
        decimals: known.decimals,
        symbol: known.symbol ?? "",
        name: known.name ?? ""
      };
      chainCache.set(lower, meta);
      map.set(lower, meta);
      continue;
    }
    missing.push(lower);
  }
  if (missing.length === 0) return map;
  const calls = missing.flatMap((addr) => [
    { address: addr, abi: ERC20_ABI, functionName: "decimals" },
    { address: addr, abi: ERC20_ABI, functionName: "symbol" },
    { address: addr, abi: ERC20_ABI, functionName: "name" }
  ]);
  const results = await client.multicall({ contracts: calls, allowFailure: true });
  for (let i = 0; i < missing.length; i++) {
    const decRes = results[i * 3];
    const symRes = results[i * 3 + 1];
    const nameRes = results[i * 3 + 2];
    if (decRes.status !== "success") continue;
    const meta = {
      decimals: Number(decRes.result),
      symbol: symRes.status === "success" ? String(symRes.result) : "",
      name: nameRes.status === "success" ? String(nameRes.result) : ""
    };
    const lower = missing[i].toLowerCase();
    chainCache.set(lower, meta);
    map.set(lower, meta);
  }
  return map;
}
async function probePairs(client, factory, pairsToProbe) {
  if (pairsToProbe.length === 0) return [];
  const calls = pairsToProbe.flatMap(([a, b]) => [
    {
      address: factory,
      abi: PAIR_FACTORY_ABI,
      functionName: "getPool",
      args: [a, b, false]
    },
    {
      address: factory,
      abi: PAIR_FACTORY_ABI,
      functionName: "getPool",
      args: [a, b, true]
    }
  ]);
  const results = await client.multicall({ contracts: calls, allowFailure: true });
  const found = [];
  const seenAddresses = /* @__PURE__ */ new Set();
  for (let i = 0; i < pairsToProbe.length; i++) {
    const [tokenA, tokenB] = pairsToProbe[i];
    for (let s = 0; s < 2; s++) {
      const res = results[i * 2 + s];
      if (res.status !== "success") continue;
      const pairAddress = String(res.result).toLowerCase();
      if (pairAddress === ZERO_ADDRESS || seenAddresses.has(pairAddress)) continue;
      seenAddresses.add(pairAddress);
      found.push({ pairAddress, tokenA, tokenB, stable: s === 1 });
    }
  }
  return found;
}
async function loadReservesAndOrdering(client, pairs) {
  if (pairs.length === 0) return [];
  const calls = pairs.flatMap((p) => [
    { address: p.pairAddress, abi: PAIR_ABI, functionName: "getReserves" },
    { address: p.pairAddress, abi: PAIR_ABI, functionName: "token0" }
  ]);
  const results = await client.multicall({ contracts: calls, allowFailure: true });
  const out = [];
  for (let i = 0; i < pairs.length; i++) {
    const reservesRes = results[i * 2];
    const token0Res = results[i * 2 + 1];
    if (reservesRes.status !== "success" || token0Res.status !== "success") continue;
    const reserves = reservesRes.result;
    const token0Addr = String(token0Res.result).toLowerCase();
    const tokenA = pairs[i].tokenA.toLowerCase();
    const tokenB = pairs[i].tokenB.toLowerCase();
    const token1Addr = token0Addr === tokenA ? tokenB : tokenA;
    out.push({
      pairAddress: pairs[i].pairAddress,
      token0: token0Addr,
      token1: token1Addr,
      reserve0Raw: reserves[0],
      reserve1Raw: reserves[1]
    });
  }
  return out;
}
async function buildPoolsFromProbedPairs(client, chainId, probed) {
  if (probed.length === 0) return [];
  const pairData = await loadReservesAndOrdering(client, probed);
  if (pairData.length === 0) return [];
  const stableByAddress = /* @__PURE__ */ new Map();
  for (const p of probed) {
    stableByAddress.set(p.pairAddress.toLowerCase(), p.stable);
  }
  const tokenAddresses = Array.from(new Set(pairData.flatMap((p) => [p.token0, p.token1])));
  const metadata = await loadTokenMetadata(client, chainId, tokenAddresses);
  const pools = [];
  for (const p of pairData) {
    if (p.reserve0Raw === 0n || p.reserve1Raw === 0n) continue;
    const meta0 = metadata.get(p.token0);
    const meta1 = metadata.get(p.token1);
    if (!meta0 || !meta1) continue;
    pools.push({
      id: p.pairAddress,
      token0: {
        id: p.token0,
        symbol: meta0.symbol,
        decimals: String(meta0.decimals),
        name: meta0.name
      },
      token1: {
        id: p.token1,
        symbol: meta1.symbol,
        decimals: String(meta1.decimals),
        name: meta1.name
      },
      reserve0: formatUnits(p.reserve0Raw, meta0.decimals),
      reserve1: formatUnits(p.reserve1Raw, meta1.decimals),
      reserveUSD: "0",
      // No on-chain USD pricing without a subgraph
      stable: stableByAddress.get(p.pairAddress.toLowerCase()) ?? false
    });
  }
  return pools;
}
export async function fetchV2PoolsOnChain(tokenIn, tokenOut, chainId) {
  const contracts = getBscContracts(chainId);
  if (!contracts?.poolFactory || contracts.poolFactory === ZERO_ADDRESS) return [];
  const client = makeClient(chainId);
  if (!client) return [];
  const a = tokenIn.toLowerCase();
  const b = tokenOut.toLowerCase();
  const intermediaries = getCommonIntermediaries(chainId).map((x) => x.toLowerCase()).filter((x) => x !== a && x !== b && x !== ZERO_ADDRESS);
  const pairsToProbe = [[a, b]];
  for (const base of intermediaries) {
    pairsToProbe.push([a, base]);
    pairsToProbe.push([base, b]);
  }
  try {
    const probed = await probePairs(client, contracts.poolFactory, dedupePairs(pairsToProbe));
    const pools = await buildPoolsFromProbedPairs(client, chainId, probed);
    return pools;
  } catch (error) {
    routingError("fetchV2PoolsOnChain failed", error);
    return [];
  }
}
export async function fetchV2MultiHopPoolsOnChain(tokens, chainId) {
  const contracts = getBscContracts(chainId);
  if (!contracts?.poolFactory || contracts.poolFactory === ZERO_ADDRESS) return [];
  const client = makeClient(chainId);
  if (!client) return [];
  const lowered = Array.from(new Set(tokens.map((t) => t.toLowerCase()))).filter(
    (t) => t && t !== ZERO_ADDRESS
  );
  const pairsToProbe = [];
  for (let i = 0; i < lowered.length; i++) {
    for (let j = i + 1; j < lowered.length; j++) {
      pairsToProbe.push([lowered[i], lowered[j]]);
    }
  }
  try {
    const probed = await probePairs(client, contracts.poolFactory, dedupePairs(pairsToProbe));
    return await buildPoolsFromProbedPairs(client, chainId, probed);
  } catch (error) {
    routingError("fetchV2MultiHopPoolsOnChain failed", error);
    return [];
  }
}
export function shouldUseOnChainV2(chainId) {
  return chainId === UniverseChainId.Sepolia;
}
const PAIR_STABLE_ABI = [
  ...PAIR_ABI,
  { type: "function", name: "stable", stateMutability: "view", inputs: [], outputs: [{ type: "bool" }] }
];
const V3_POOL_ABI = [
  { type: "function", name: "token0", stateMutability: "view", inputs: [], outputs: [{ type: "address" }] },
  { type: "function", name: "token1", stateMutability: "view", inputs: [], outputs: [{ type: "address" }] },
  { type: "function", name: "tickSpacing", stateMutability: "view", inputs: [], outputs: [{ type: "int24" }] },
  { type: "function", name: "liquidity", stateMutability: "view", inputs: [], outputs: [{ type: "uint128" }] }
];
function toCompatToken(address, meta) {
  return {
    id: address,
    symbol: meta.symbol,
    name: meta.name,
    decimals: String(meta.decimals)
  };
}
export async function fetchV2PoolMetadataOnChain(poolAddresses, chainId) {
  const lowered = Array.from(new Set(poolAddresses.map((p) => p.toLowerCase()))).filter(
    (p) => p && p !== ZERO_ADDRESS
  );
  if (lowered.length === 0) return [];
  const client = makeClient(chainId);
  if (!client) return [];
  try {
    const pairCalls = lowered.flatMap((pool) => [
      { address: pool, abi: PAIR_STABLE_ABI, functionName: "token0" },
      { address: pool, abi: PAIR_STABLE_ABI, functionName: "token1" },
      { address: pool, abi: PAIR_STABLE_ABI, functionName: "getReserves" },
      { address: pool, abi: PAIR_STABLE_ABI, functionName: "stable" }
    ]);
    const pairResults = await client.multicall({ contracts: pairCalls, allowFailure: true });
    const raw = [];
    for (let i = 0; i < lowered.length; i++) {
      const t0Res = pairResults[i * 4];
      const t1Res = pairResults[i * 4 + 1];
      const reservesRes = pairResults[i * 4 + 2];
      const stableRes = pairResults[i * 4 + 3];
      if (t0Res.status !== "success" || t1Res.status !== "success" || reservesRes.status !== "success") continue;
      const reserves = reservesRes.result;
      raw.push({
        pool: lowered[i],
        token0: String(t0Res.result).toLowerCase(),
        token1: String(t1Res.result).toLowerCase(),
        reserve0Raw: reserves[0],
        reserve1Raw: reserves[1],
        // stable() may not exist on non-Velodrome pairs — default to false on failure
        stable: stableRes.status === "success" ? Boolean(stableRes.result) : (() => {
          routingWarn("stable() call failed for pool \u2014 defaulting to volatile (false)", lowered[i]);
          return false;
        })()
      });
    }
    if (raw.length === 0) return [];
    const tokenAddresses = Array.from(new Set(raw.flatMap((r) => [r.token0, r.token1])));
    const metadata = await loadTokenMetadata(client, chainId, tokenAddresses);
    return raw.map((r) => {
      const meta0 = metadata.get(r.token0);
      const meta1 = metadata.get(r.token1);
      if (!meta0 || !meta1) return null;
      return {
        id: r.pool,
        token0: toCompatToken(r.token0, meta0),
        token1: toCompatToken(r.token1, meta1),
        reserve0: formatUnits(r.reserve0Raw, meta0.decimals),
        reserve1: formatUnits(r.reserve1Raw, meta1.decimals),
        reserveUSD: "0",
        // no on-chain pricing
        volumeUSD: "0",
        // no on-chain pricing
        stable: r.stable
      };
    }).filter((p) => p !== null);
  } catch (error) {
    routingError("fetchV2PoolMetadataOnChain failed", error);
    return [];
  }
}
export async function fetchV3PoolMetadataOnChain(poolAddresses, chainId) {
  const lowered = Array.from(new Set(poolAddresses.map((p) => p.toLowerCase()))).filter(
    (p) => p && p !== ZERO_ADDRESS
  );
  if (lowered.length === 0) return [];
  const client = makeClient(chainId);
  if (!client) return [];
  try {
    const poolCalls = lowered.flatMap((pool) => [
      { address: pool, abi: V3_POOL_ABI, functionName: "token0" },
      { address: pool, abi: V3_POOL_ABI, functionName: "token1" },
      { address: pool, abi: V3_POOL_ABI, functionName: "tickSpacing" },
      { address: pool, abi: V3_POOL_ABI, functionName: "liquidity" }
    ]);
    const poolResults = await client.multicall({ contracts: poolCalls, allowFailure: true });
    const raw = [];
    for (let i = 0; i < lowered.length; i++) {
      const t0Res = poolResults[i * 4];
      const t1Res = poolResults[i * 4 + 1];
      const tsRes = poolResults[i * 4 + 2];
      const liqRes = poolResults[i * 4 + 3];
      if (t0Res.status !== "success" || t1Res.status !== "success" || tsRes.status !== "success") continue;
      raw.push({
        pool: lowered[i],
        token0: String(t0Res.result).toLowerCase(),
        token1: String(t1Res.result).toLowerCase(),
        tickSpacing: String(tsRes.result),
        liquidity: liqRes.status === "success" ? String(liqRes.result) : "0"
      });
    }
    if (raw.length === 0) return [];
    const tokenAddresses = Array.from(new Set(raw.flatMap((r) => [r.token0, r.token1])));
    const metadata = await loadTokenMetadata(client, chainId, tokenAddresses);
    return raw.map((r) => {
      const meta0 = metadata.get(r.token0);
      const meta1 = metadata.get(r.token1);
      if (!meta0 || !meta1) return null;
      return {
        id: r.pool,
        token0: toCompatToken(r.token0, meta0),
        token1: toCompatToken(r.token1, meta1),
        tickSpacing: r.tickSpacing,
        liquidity: r.liquidity,
        sqrtPrice: "0",
        tick: "0",
        totalValueLockedUSD: "0",
        totalValueLockedToken0: "0",
        totalValueLockedToken1: "0",
        volumeUSD: "0"
      };
    }).filter((p) => p !== null);
  } catch (error) {
    routingError("fetchV3PoolMetadataOnChain failed", error);
    return [];
  }
}
const CL_FACTORY_ABI = [
  {
    type: "function",
    name: "getPool",
    stateMutability: "view",
    inputs: [
      { name: "tokenA", type: "address" },
      { name: "tokenB", type: "address" },
      { name: "tickSpacing", type: "int24" }
    ],
    outputs: [{ name: "pool", type: "address" }]
  }
];
const V3_TICK_SPACINGS = [1, 50, 100, 200, 2e3];
export async function fetchV3MultiHopPoolsOnChain(tokens, chainId) {
  const contracts = getBscContracts(chainId);
  const clFactory = contracts?.clFactory;
  if (!clFactory || clFactory === ZERO_ADDRESS) return [];
  const client = makeClient(chainId);
  if (!client) return [];
  const pairs = dedupePairs(
    tokens.flatMap((a) => tokens.map((b) => [a, b]))
  );
  if (pairs.length === 0) return [];
  try {
    const calls = pairs.flatMap(
      ([a, b]) => V3_TICK_SPACINGS.map((tickSpacing) => ({
        address: clFactory,
        abi: CL_FACTORY_ABI,
        functionName: "getPool",
        args: [a, b, tickSpacing]
      }))
    );
    const results = await client.multicall({ contracts: calls, allowFailure: true });
    const poolAddresses = [];
    for (const r of results) {
      if (r.status !== "success") continue;
      const addr = String(r.result).toLowerCase();
      if (addr && addr !== ZERO_ADDRESS) poolAddresses.push(addr);
    }
    if (poolAddresses.length === 0) return [];
    return await fetchV3PoolMetadataOnChain(poolAddresses, chainId);
  } catch (error) {
    routingError("fetchV3MultiHopPoolsOnChain failed", error);
    return [];
  }
}
